import { useState } from "react";
import { X, Check } from "lucide-react";
import api from "../../../../../utils/axiosInstance";
import { toast } from "../../../../../components/Toast/toast";
import { btn } from "../../../../../components/Button/buttonStyles";
import DownloadZipButton from "../../../../../components/Button/DownloadZipButton";

// Selection Modal

const AddToProjectOutputModal = ({ files, onClose, onConfirm }) => {
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const eligibleFiles = files.filter((f) => !f.is_project_output);

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selected.length === eligibleFiles.length) {
      setSelected([]);
    } else {
      setSelected(eligibleFiles.map((f) => f.id));
    }
  };

  const handleConfirm = async () => {
    if (selected.length === 0) return;
    setSubmitting(true);
    await onConfirm(selected);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-base font-semibold text-gray-800">
            Select Files to Add to Project Output
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {eligibleFiles.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              All files have already been added to project output.
            </p>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b text-xs text-gray-600">
                  <th className="px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={selected.length === eligibleFiles.length && eligibleFiles.length > 0}
                      onChange={toggleAll}
                      className="cursor-pointer"
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">File #</th>
                  <th className="px-3 py-2 text-left font-semibold">Filename</th>
                  <th className="px-3 py-2 text-left font-semibold">Size</th>
                  <th className="px-3 py-2 text-left font-semibold">Job</th>
                </tr>
              </thead>
              <tbody>
                {eligibleFiles.map((file, index) => {
                  const isChecked = selected.includes(file.id);
                  const kb = (file.file_size || 0) / 1024;
                  const size = kb < 1024 ? `${kb.toFixed(2)} KB` : `${(kb / 1024).toFixed(2)} MB`;

                  return (
                    <tr
                      key={file.id}
                      onClick={() => toggleSelect(file.id)}
                      className={`cursor-pointer border-b transition ${
                        isChecked
                          ? "bg-brand-50"
                          : index % 2 === 0
                          ? "bg-white hover:bg-gray-50"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelect(file.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {file.file_code || `#${index + 1}`}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-800 break-words">
                        {file.original_file_name || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                        {file.file_size ? size : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                        {file.job?.name || `Job #${file.job_id}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between gap-3">
          <span className="text-xs text-gray-500">
            {selected.length} file{selected.length !== 1 ? "s" : ""} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className={btn("secondary", "sm")}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected.length === 0 || submitting}
              className={btn("primary", "sm")}
            >
              <Check className="w-4 h-4" />
              {submitting ? "Adding..." : "Add to Project Output"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Card

const OutputFilesCard = ({ projectId, files = [], onRefresh }) => {
  const [showModal, setShowModal] = useState(false);

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  const hasFiles = files.length > 0;
  const allAdded = hasFiles && files.every((f) => f.is_project_output);

  const handleConfirmAdd = async (selectedIds) => {
    try {
      await Promise.all(
        selectedIds.map((id) =>
          api.post(`/job-output-files/${id}/add-to-project`, {}, { withCredentials: true })
        )
      );
      setShowModal(false);
      onRefresh();
    } catch (error) {
      console.error("Add to project output error:", error);
      toast.error(error.response?.data?.message || "Failed to add files to project output");
    }
  };

  const HEADERS = ["File #", "Filename", "Size", "Input for Jobs", "Project Output"];

  return (
    <>
      <section>
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-bold text-gray-900">
            Linked Output Files <span className="text-gray-500 font-medium">({files.length})</span>
          </h2>

          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => setShowModal(true)}
              disabled={!hasFiles || allAdded}
              title={
                !hasFiles
                  ? "No job output files available"
                  : allAdded
                  ? "All files already added to project output"
                  : "Select files to add to project output"
              }
              className={btn("primary", "sm")}
            >
              Add to Project Output
            </button>
            <DownloadZipButton
              url={`/job-output-files/download-zip-by-project?project_id=${projectId}`}
              fileName={`project-${projectId}-linked-output-files.zip`}
              disabled={!hasFiles}
            />
          </div>
        </div>

        <div className="w-full bg-white shadow rounded-lg overflow-hidden">
          <div className="max-h-[300px] overflow-y-auto overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                  {HEADERS.map((h) => (
                    <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {files.length === 0 ? (
                  <tr>
                    <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                      No job output files found.
                    </td>
                  </tr>
                ) : (
                  files.map((file, index) => {
                    const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                    return (
                      <tr key={file.id || index} className={`${rowBg} hover:bg-brand-50 transition-colors`}>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                          {file.file_code || `#${index + 1}`}
                        </td>
                        <td className="px-3 py-2 text-xs text-center break-words text-gray-900">
                          {file.original_file_name || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                          {formatFileSize(file.file_size)}
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-700">
                          {file.input_for_job || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap">
                          {file.is_project_output ? (
                            <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                              ✓ Added
                            </span>
                          ) : (
                            <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                              Not added
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {showModal && (
        <AddToProjectOutputModal
          files={files}
          onClose={() => setShowModal(false)}
          onConfirm={handleConfirmAdd}
        />
      )}
    </>
  );
};

export default OutputFilesCard;